import { useState } from "react";
import { Logo } from "../atoms/Logo.jsx";
import { apiFetch, API_BASE } from "../services/api.js";
import { supabase } from "../lib/supabase.js";

export function AdminLogin({onSuccess, onPoLogin, onSignup, onCancel}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("password"); // "password" | "magic-link"
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  const handleSendMagicLink = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setErr(true); setErrMsg("Enter your email"); return; }
    setLoading(true);
    setErr(false);
    setErrMsg("");

    try {
      // Store admin magic link intent so auth-callback can detect it
      sessionStorage.setItem("qm_admin_magic_link", "1");
      const redirectUrl = `${window.location.origin}/auth/callback`;
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: { emailRedirectTo: redirectUrl },
      });
      if (error) {
        if (error.code === "over_email_send_rate_limit" || error.message?.toLowerCase().includes("rate limit")) {
          setMagicLinkSent(true);
          setErrMsg("A magic link was already sent. Check your inbox or wait to resend.");
          setLoading(false);
          return;
        }
        throw error;
      }
      setMagicLinkSent(true);
    } catch (e) {
      setErr(true);
      setErrMsg(e.message || "Failed to send magic link");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === "magic-link") return handleSendMagicLink(e);

    setLoading(true);
    setErr(false);
    setErrMsg("");

    // Try Supabase Auth first (email/password)
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (!error && data?.session) {
        // Detect role via /api/auth/me before routing.
        // Pass the JWT explicitly since the module-level token
        // from onAuthStateChange may not have propagated yet.
        const jwt = data.session.access_token;
        const secret = import.meta.env.VITE_API_SECRET ?? "";
        const authHeaders = {
          "content-type": "application/json",
          "X-API-Secret": secret,
          "Authorization": `Bearer ${jwt}`,
        };
        try {
          const res = await fetch(`${API_BASE}/api/auth/me`, { headers: authHeaders });
          if (res.ok) {
            const me = await res.json();
            if (me.role === "persona_owner") {
              // PO login — route to claim flow or PO dashboard
              onPoLogin?.({ personaId: me.personaId, email: me.email });
              return;
            }
            if (me.role === "admin") {
              sessionStorage.setItem("qm_admin", "1");
              sessionStorage.setItem("qm_admin_token", jwt);
              onSuccess();
              return;
            }
            // Hotfix v1.27.3 — `role === "citizen"` means `user_roles` has
            // no admin row for this user. Before this fix, the code fell
            // through and called onSuccess() anyway in case the user was
            // an `ADMIN_USER` env-var bootstrap admin. That gamble caused
            // a silent login loop if the user was ALSO not the env-var
            // admin: set qm_admin=1 → effect fired getAdminPersonas →
            // backend adminAuth said 403 → apiFetch 401 handler wiped
            // sessionStorage + redirected to /admin?next=… → repeat.
            //
            // Probe the admin middleware directly with raw fetch (bypasses
            // apiFetch's auto-wipe-on-403). If the probe returns 200 we
            // know adminAuth accepts this user — matches the ADMIN_USER
            // bootstrap case — and we proceed. If it 403s, we surface a
            // clean "not admin" error instead of looping.
            //
            // /api/admin/health is a cheap admin-auth'd endpoint (just
            // returns {ok:true}). Using it instead of /api/admin/personas
            // avoids the schema query side-effect and makes the probe
            // fast.
            const probe = await fetch(`${API_BASE}/api/admin/health`, { headers: authHeaders });
            if (probe.ok) {
              // Env-var admin bootstrap path — adminAuth accepts the
              // email match even without a user_roles row.
              sessionStorage.setItem("qm_admin", "1");
              sessionStorage.setItem("qm_admin_token", jwt);
              onSuccess();
              return;
            }
            // Probe 403 / 401 — this account is genuinely not an admin.
            // Don't set qm_admin. Sign out the Supabase session we just
            // created so the user isn't left in a half-authenticated
            // state where their next page load thinks they're logged in
            // but every admin fetch 403s.
            try { await supabase.auth.signOut(); } catch { /* best-effort */ }
            setErr(true);
            setErrMsg("This account does not have admin access.");
            return;
          }
        } catch {
          // /api/auth/me network error — fall through to legacy password
          // path below (doesn't require Supabase to be reachable).
        }
        // /api/auth/me returned a non-ok status (not a thrown error).
        // Sign out the transient Supabase session and fall through to
        // the legacy X-Admin-Token password flow.
        try { await supabase.auth.signOut(); } catch { /* best-effort */ }
      }
    } catch {
      // Supabase Auth failed — fall through to legacy
    }

    // Legacy fallback: use old admin-login endpoint (username/password via env vars)
    try {
      const { token } = await apiFetch("/api/auth/admin-login", {
        method: "POST",
        body: JSON.stringify({ username: email, password }),
      });
      sessionStorage.setItem("qm_admin_token", token);
      onSuccess();
    } catch {
      setErr(true);
      setErrMsg("Incorrect email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{minHeight:"100vh",background:"#090e1a",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"24px 20px"}}>
      <div style={{width:"100%",maxWidth:380,animation:"fadeUp .4s ease"}}>
        <div style={{textAlign:"center",marginBottom:36}}>
          <div style={{margin:"0 auto 14px",display:"flex",justifyContent:"center"}}><Logo size={32} /></div>
        </div>
        <div style={{marginBottom:28}}>
          <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.15em",color:"#3b82f6",textTransform:"uppercase",marginBottom:6}}>QuestionMe</div>
          <h2 style={{color:"#f1f5f9",fontSize:22,fontWeight:900,fontFamily:"Georgia,serif",margin:0}}>Sign In</h2>
          <p style={{color:"#64748b",fontSize:13,marginTop:6}}>Admin or Persona Owner</p>
        </div>
        {magicLinkSent ? (
          <div style={{textAlign:"center",animation:"fadeUp .3s ease"}}>
            <div style={{fontSize:48,marginBottom:16}}>&#x2709;&#xFE0F;</div>
            <h3 style={{color:"#f1f5f9",fontSize:18,fontWeight:800,fontFamily:"Georgia,serif",margin:"0 0 10px"}}>Check your email</h3>
            <p style={{color:"#64748b",fontSize:14,lineHeight:1.6,margin:"0 0 8px"}}>
              We sent a magic link to <strong style={{color:"#e2e8f0"}}>{email}</strong>
            </p>
            <p style={{color:"#475569",fontSize:13,lineHeight:1.6,margin:"0 0 20px"}}>
              Click the link in the email to sign in. You can close this tab.
            </p>
            {errMsg && <div style={{color:"#f59e0b",fontSize:13,marginBottom:16}}>{errMsg}</div>}
            <button type="button" onClick={() => { setMagicLinkSent(false); setErrMsg(""); }} style={{padding:"12px 24px",background:"transparent",border:"1px solid #1e2d45",color:"#64748b",borderRadius:11,fontSize:14,cursor:"pointer",fontFamily:"inherit"}}>
              Back to sign in
            </button>
          </div>
        ) : (
        <form onSubmit={handleSubmit} style={{display:"flex",flexDirection:"column",gap:14}}>
          <div>
            <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"#64748b",textTransform:"uppercase",marginBottom:8}}>Email</div>
            <input
              autoFocus
              type="email"
              value={email}
              onChange={e=>{setEmail(e.target.value);setErr(false);}}
              placeholder="admin@questionme.io"
              style={{width:"100%",padding:"13px 16px",background:"#0d1724",border:`1px solid ${err?"#ef4444":"#1e2d45"}`,borderRadius:11,color:"#e2e8f0",fontSize:15,fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}
            />
          </div>
          {mode === "password" && (
          <div>
            <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"#64748b",textTransform:"uppercase",marginBottom:8}}>Password</div>
            <input
              type="password"
              value={password}
              onChange={e=>{setPassword(e.target.value);setErr(false);}}
              placeholder="Password"
              style={{width:"100%",padding:"13px 16px",background:"#0d1724",border:`1px solid ${err?"#ef4444":"#1e2d45"}`,borderRadius:11,color:"#e2e8f0",fontSize:15,fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}
            />
          </div>
          )}
          {err && <div style={{color:"#ef4444",fontSize:13}}>{errMsg || "Incorrect email or password"}</div>}
          <button type="submit" disabled={loading} style={{width:"100%",padding:"14px",background:"linear-gradient(135deg,#3b82f6,#2563eb)",color:"#fff",border:"none",borderRadius:11,fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"inherit",marginTop:4,opacity:loading?0.7:1}}>
            {loading ? (mode === "magic-link" ? "Sending\u2026" : "Logging in\u2026") : (mode === "magic-link" ? "Send Magic Link" : "Login")}
          </button>
          <div style={{textAlign:"center"}}>
            <button type="button" onClick={() => { setMode(mode === "password" ? "magic-link" : "password"); setErr(false); setErrMsg(""); }} style={{background:"none",border:"none",color:"#3b82f6",cursor:"pointer",padding:0,fontFamily:"inherit",fontSize:13,fontWeight:600}}>
              {mode === "password" ? "Sign in with magic link instead" : "Sign in with password instead"}
            </button>
          </div>
          {onSignup && (
            <div style={{textAlign:"center",fontSize:13,marginTop:4}}>
              <span style={{color:"#64748b"}}>Elected official? </span>
              <button type="button" onClick={onSignup} style={{background:"none",border:"none",color:"#10b981",cursor:"pointer",padding:0,fontFamily:"inherit",fontWeight:600}}>
                Claim your AI clone
              </button>
            </div>
          )}
          <button type="button" onClick={onCancel} style={{width:"100%",padding:"12px",background:"transparent",border:"1px solid #1e2d45",color:"#64748b",borderRadius:11,fontSize:14,cursor:"pointer",fontFamily:"inherit"}}>
            Back to citizen view
          </button>
        </form>
        )}
      </div>
    </div>
  );
}

export default AdminLogin;
